/**
 * Смена пароля — отдельным окном.
 *
 * Текущий пароль спрашивается не для формальности: без него тот, кто добрался
 * до открытой вкладки, сменил бы пароль и запер владельца снаружи.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuthSettings, useChangePassword } from "../api/queries";
import { PasswordStrength } from "./PasswordStrength";
import type { Strength } from "../lib/passwordStrength";
import { errorMessage } from "../lib/authForm";
import { Dialog } from "./Dialog";
import { useAuthStore } from "../stores/authStore";

export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const settings = useAuthSettings();
  const change = useChangePassword();
  const user = useAuthStore((state) => state.user);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [strength, setStrength] = useState<Strength | null>(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const minScore = settings.data?.passwordMinScore ?? 2;
  const weak = next.length > 0 && strength !== null && !strength.acceptable;
  const mismatch = confirm.length > 0 && confirm !== next;

  const submit = async () => {
    setError(null);
    try {
      await change.mutateAsync({ currentPassword: current, newPassword: next });
      setDone(true);
    } catch (failure) {
      setError(errorMessage(failure));
    }
  };

  return (
    <Dialog title={t("profile.passwordTitle")} onClose={onClose}>
      {done ? (
        <div className="space-y-4">
          <p className="rounded-xl border border-sage/40 bg-sage/10 px-3 py-2 text-sm text-sage">
            {t("profile.passwordChanged")}
          </p>
          <button type="button" className="btn-primary w-full" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="label" htmlFor="current-password">
              {t("profile.currentPassword")}
            </label>
            <input
              id="current-password"
              type="password"
              className="field"
              autoComplete="current-password"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </div>

          <div>
            <label className="label" htmlFor="new-password">
              {t("profile.newPassword")}
            </label>
            <input
              id="new-password"
              type="password"
              className="field"
              autoComplete="new-password"
              value={next}
              onChange={(event) => setNext(event.target.value)}
            />
            <PasswordStrength
              password={next}
              userInputs={[user?.username ?? "", user?.email ?? ""]}
              minScore={minScore}
              onChange={setStrength}
            />
          </div>

          <div>
            <label className="label" htmlFor="confirm-password">
              {t("auth.field.passwordConfirm")}
            </label>
            <input
              id="confirm-password"
              type="password"
              className="field"
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
            />
            {mismatch && <p className="mt-1 text-xs text-rust">{t("auth.error.passwordMismatch")}</p>}
          </div>

          {error && <p className="text-xs text-rust">{error}</p>}

          <button
            type="button"
            className="btn-primary w-full"
            disabled={!current || !next || mismatch || weak || change.isPending}
            onClick={() => void submit()}
          >
            {change.isPending ? t("profile.saving") : t("profile.changePassword")}
          </button>
        </div>
      )}
    </Dialog>
  );
}
